import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getSessionsByDateRange } from "../Services/CartApi";
import { useOrganisations } from "./../Auth/useOrganisations";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactPaginate from "react-paginate";
import GifLoader from "../Components/loader";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getUsers } from "../Services/UserApi";
import { toast } from "react-toastify";
import OrderDetailsModal from "../Components/OrderDetails";

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxWidth: "90vw",
  bgcolor: "white",
  p: 4,
  borderRadius: 2,
};

const ROWS_PER_PAGE = 10;

function Orders() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [displayedCart, setDisplayedCart] = useState(null);
  const [currency, setCurrency] = useState("");
  const [users, setUsers] = useState([]);

  // Form state - this is what the user interacts with
  const [formFilters, setFormFilters] = useState({
    caissier_id: "",
    serveur_id: "",
    date_debut: moment().startOf("day").format("YYYY-MM-DD"),
    date_fin: moment().endOf("day").format("YYYY-MM-DD"),
    search: "",
  });

  // Applied filters - this is what's used for API calls
  const [appliedFilters, setAppliedFilters] = useState({
    caissier_id: "",
    serveur_id: "",
    date_debut: moment().startOf("day").format("YYYY-MM-DD"),
    date_fin: moment().endOf("day").format("YYYY-MM-DD"),
    page: 1,
    limit: 50,
    search: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: ROWS_PER_PAGE,
  });

  const [organisations, setOrganisations, usedOrganisations] =
    useOrganisations();

  // Load users for filters
  const loadUsers = useCallback(async () => {
    if (!usedOrganisations) return;
    try {
      const response = await getUsers(usedOrganisations);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  }, [usedOrganisations]);

  // Load sessions with applied filters
  const loadSessions = useCallback(async () => {
    if (!usedOrganisations) return;

    try {
      setLoading(true);

      // Prepare filter object for API
      const apiFilters = {
        organisation_id: usedOrganisations,
        date_debut: moment(appliedFilters.date_debut)
          .startOf("day")
          .toISOString(),
        date_fin: moment(appliedFilters.date_fin).endOf("day").toISOString(),
        page: appliedFilters.page,
        limit: appliedFilters.limit,
      };

      // Add user filters if selected
      if (appliedFilters.caissier_id) {
        apiFilters.caissier_id = appliedFilters.caissier_id;
      }
      if (appliedFilters.serveur_id) {
        apiFilters.serveur_id = appliedFilters.serveur_id;
      }
      if (appliedFilters.search) {
        apiFilters.search = appliedFilters.search;
      }

      console.log("Fetching with applied filters:", apiFilters);

      const response = await getSessionsByDateRange(apiFilters);

      if (response && response.data && response.data.success) {
        setSessions(response.data.data.allSessions || []);

        // Update pagination from API response
        if (response.data.data.pagination) {
          setPagination({
            currentPage: response.data.data.pagination.currentPage - 1,
            totalPages: response.data.data.pagination.totalPages,
            totalItems: response.data.data.pagination.totalItems,
            itemsPerPage: response.data.data.pagination.itemsPerPage,
          });
        }
      } else {
        console.error("Invalid response format:", response);
        setSessions([]);
      }
    } catch (error) {
      console.error("Error loading sessions:", error);
      toast.error("Erreur lors du chargement des commandes");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [usedOrganisations, appliedFilters]);

  // Initial load
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load sessions when appliedFilters change (only when Apply button is clicked)
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Handle form filter changes (updates UI only)
  const handleFilterChange = (key, value) => {
    setFormFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Apply filters - copies form filters to applied filters and resets page to 1
  const handleApplyFilters = () => {
    setAppliedFilters({
      ...formFilters,
      page: 1, // Reset to first page when applying new filters
      limit: 50,
    });
  };

  // Reset all filters
  const handleResetFilters = () => {
    const defaultFilters = {
      caissier_id: "",
      serveur_id: "",
      date_debut: moment().startOf("day").format("YYYY-MM-DD"),
      date_fin: moment().endOf("day").format("YYYY-MM-DD"),
      search: "",
    };

    setFormFilters(defaultFilters);
    setAppliedFilters({
      ...defaultFilters,
      page: 1,
      limit: 50,
    });
  };

  // Handle page change
  const handlePageChange = (data) => {
    const newPage = data.selected + 1; // Convert to 1-based for API
    setAppliedFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  // Flatten all carts from all sessions for table display
  const allCarts = useMemo(() => {
    if (!sessions.length) return [];
    console.log(sessions);
    const carts = [];
    sessions.forEach((sessionItem) => {
      if (sessionItem.carts && sessionItem.carts.length > 0) {
        sessionItem.carts.forEach((cart) => {
          carts.push({
            ...cart,
            session: sessionItem.session,
            payments: sessionItem.payments || [],
            sessionId: sessionItem.session?._id,
            tableNumber:
              sessionItem.session?.table_id?.table_number ||
              sessionItem.session?.table_id?.numero ||
              cart.table_id?.numero,
          });
        });
      }
    });
    return carts;
  }, [sessions]);

  // Modal handlers
  const displayOrderModal = (cart) => {
    setDisplayedCart(cart);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDisplayedCart(null);
  };

  const handlePrint = () => {
    const printButton = document.getElementById("printButton");
    if (printButton) {
      printButton.style.display = "none";
    }

    window.print();

    if (printButton) {
      printButton.style.display = "block";
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    const date = moment(dateString);
    return {
      date: date.format("DD-MM-YYYY"),
      time: date.format("HH:mm"),
      full: date.format("DD-MM-YYYY HH:mm"),
    };
  };

  return (
    <div className="container-fluid">
      <div className="container-full">
        <div className="content-header">
          <div className="d-flex align-items-center">
            <div className="mr-auto">
              <h3 className="page-title">{t("Liste des commandes")}</h3>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-2">
                      <div className="form-group">
                        <label>{t("Date de début")}</label>
                        <DatePicker
                          className="form-control"
                          selected={
                            formFilters.date_debut
                              ? moment(formFilters.date_debut).toDate()
                              : null
                          }
                          onChange={(date) =>
                            handleFilterChange(
                              "date_debut",
                              date ? moment(date).format("YYYY-MM-DD") : "",
                            )
                          }
                          selectsStart
                          startDate={
                            formFilters.date_debut
                              ? moment(formFilters.date_debut).toDate()
                              : null
                          }
                          endDate={
                            formFilters.date_fin
                              ? moment(formFilters.date_fin).toDate()
                              : null
                          }
                          dateFormat="dd-MM-yyyy"
                          placeholderText={t("Date de début")}
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="form-group">
                        <label>{t("Date de fin")}</label>
                        <DatePicker
                          className="form-control"
                          selected={
                            formFilters.date_fin
                              ? moment(formFilters.date_fin).toDate()
                              : null
                          }
                          onChange={(date) =>
                            handleFilterChange(
                              "date_fin",
                              date ? moment(date).format("YYYY-MM-DD") : "",
                            )
                          }
                          selectsEnd
                          startDate={
                            formFilters.date_debut
                              ? moment(formFilters.date_debut).toDate()
                              : null
                          }
                          endDate={
                            formFilters.date_fin
                              ? moment(formFilters.date_fin).toDate()
                              : null
                          }
                          minDate={
                            formFilters.date_debut
                              ? moment(formFilters.date_debut).toDate()
                              : null
                          }
                          dateFormat="dd-MM-yyyy"
                          placeholderText={t("Date de fin")}
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Caissier</label>
                        <select
                          className="form-control"
                          value={formFilters.caissier_id}
                          onChange={(e) =>
                            handleFilterChange("caissier_id", e.target.value)
                          }
                        >
                          <option value="">Tous les caissiers</option>
                          {users
                            .filter((user) => user.role === "caissier")
                            .map((caissier) => (
                              <option key={caissier._id} value={caissier._id}>
                                {caissier.nom} {caissier.prenom}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Serveur</label>
                        <select
                          className="form-control"
                          value={formFilters.serveur_id}
                          onChange={(e) =>
                            handleFilterChange("serveur_id", e.target.value)
                          }
                        >
                          <option value="">Tous les serveurs</option>
                          {users
                            .filter((user) => user.role === "serveur")
                            .map((serveur) => (
                              <option key={serveur._id} value={serveur._id}>
                                {serveur.nom} {serveur.prenom}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Recherche par code</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Rechercher par code commande..."
                          value={formFilters.search}
                          onChange={(e) =>
                            handleFilterChange("search", e.target.value)
                          }
                          onKeyPress={handleSearchKeyPress}
                        />
                      </div>
                    </div>

                    {/* Action buttons — kept inline with the filters, not on their own row */}
                    <div className="col-md-1">
                      <div className="form-group">
                        <label>&nbsp;</label>
                        <div className="d-flex" style={{ gap: "8px" }}>
                          <button
                            className="btn btn-primary"
                            onClick={handleApplyFilters}
                            title="Appliquer les filtres"
                          >
                            <i className="fa fa-filter"></i>
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={handleResetFilters}
                            title="Réinitialiser les filtres"
                          >
                            <i className="fas fa-undo"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Active filters display */}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2">Chargement des commandes...</p>
                </div>
              ) : allCarts.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h5>Aucune commande trouvée</h5>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>{t("Code")}</th>
                          <th>{t("Table")}</th>
                          <th>{t("Serveur")}</th>
                          <th>{t("Caissier")}</th>
                          <th>{t("Date")}</th>
                          <th>{t("Total")}</th>
                          <th>{t("Statut")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCarts.map((cart) => {
                          const formattedDate = formatDateTime(cart.createdAt);
                          return (
                            <tr key={`cart-${cart._id || cart.id}`}>
                              <td>{cart.session?.code || cart.code}</td>
                              <td>
                                {cart.tableNumber === "0"
                                  ? t("Comptoir")
                                  : cart.tableNumber}
                              </td>{" "}
                              <td>
                                {cart.session?.serveur_id?.nom
                                  ? `${cart.session.serveur_id.nom} ${cart.session.serveur_id.prenom || ""}`
                                  : "-"}
                              </td>
                              <td>
                                {cart.session?.caissier_id?.nom
                                  ? `${cart.session.caissier_id.nom} ${cart.session.caissier_id.prenom || ""}`
                                  : "-"}
                              </td>
                              <td>
                                {formattedDate.date}{" "}
                                <span className="font-size-12">
                                  {formattedDate.time}
                                </span>
                              </td>
                              <td>
                                {(cart.prix || cart.totalAmount || 0).toFixed(
                                  3,
                                )}{" "}
                                {currency}
                              </td>
                              <td>
                                <span
                                  className={`badge badge-pill badge-${
                                    cart.session?.status === "inactive"
                                      ? "success"
                                      : "warning"
                                  }-light`}
                                >
                                  {cart.session?.status === "inactive"
                                    ? t("Payée")
                                    : t("En cours")}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => displayOrderModal(cart)}
                                >
                                  <i className="fa fa-eye"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="row mt-4">
                      <div className="col-12 d-flex justify-content-between align-items-center">
                        <div>
                          Affichage{" "}
                          {pagination.currentPage * pagination.itemsPerPage + 1}{" "}
                          à{" "}
                          {Math.min(
                            (pagination.currentPage + 1) *
                              pagination.itemsPerPage,
                            pagination.totalItems,
                          )}{" "}
                          sur {pagination.totalItems} commandes
                        </div>
                        <ReactPaginate
                          previousLabel={"Précédent"}
                          nextLabel={"Suivant"}
                          breakLabel={"..."}
                          breakClassName={"break-me"}
                          pageCount={pagination.totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName={"pagination"}
                          subContainerClassName={"pages pagination"}
                          activeClassName={"active"}
                          forcePage={pagination.currentPage}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="content" style={{ padding: "5px 7px 0px 6px" }}>
        <GifLoader loading={loading} />

        <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .MuiBox-root, .MuiBox-root * {
            visibility: visible;
          }
          .MuiBox-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
            box-shadow: none;
          }
        }
      `}</style>
      </section>
      <OrderDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        cart={displayedCart}
        currency={currency}
        t={t}
        onPrint={handlePrint}
      />
    </div>
  );
}

export default Orders;
